/**
 * Tauri command wrappers for type-safe invocation.
 *
 * Every function here fronts a Rust command and is therefore **desktop-only**.
 * The browser portal has no Rust layer; the surfaces it ships either do not call
 * these at all or branch on `IS_TAURI` before doing so (see
 * `shared/stores/operator-store.ts`, which resolves the staff profile straight
 * from the gateway in a browser).
 *
 * The `@tauri-apps/api/core` import is therefore **lazy** (Task 4093). A static
 * top-level import would pull the module into the browser bundle regardless of
 * whether any of these functions is ever called, because bundlers follow imports,
 * not call graphs. Going through `tauriInvoke()` keeps the dependency inside a
 * dynamic import that Rollup drops from the web build entirely.
 *
 * The type parameter is preserved, so callers are typed exactly as before.
 */

import { IS_TAURI } from './platform'

/**
 * Lazily resolve Tauri's `invoke` and call it.
 *
 * Throws in a browser rather than returning a plausible-looking empty value: a
 * caller reaching here on the web is a bug in the platform branching, and it
 * should fail loudly at the call site rather than surface later as mystery
 * missing data.
 */
async function tauriInvoke<T>(
  command: string,
  args?: Record<string, unknown>,
): Promise<T> {
  if (!IS_TAURI) {
    throw new Error(
      `Tauri command "${command}" is not available in the browser build`,
    )
  }
  const { invoke } = await import('@tauri-apps/api/core')
  return invoke<T>(command, args)
}

/**
 * Get the raw API key for frontend use.
 * This key is injected into the TypeScript API client at app startup.
 */
export async function getApiKey(): Promise<string> {
  return tauriInvoke<string>('get_frontend_api_key');
}

// ── Dual-Environment Config ──────────────────────────────────────────────────

/** Configuration for a single environment (prod or dev). */
export interface EnvironmentConfig {
  name: string
  gateway_url: string
  api_key: string
  unavailable: boolean
  error?: string
}

/** Both environment configurations, fetched at startup. */
export interface EnvironmentConfigs {
  prod: EnvironmentConfig
  dev: EnvironmentConfig
}

/**
 * Get both environment configurations (prod + dev) for a specific operator.
 *
 * Pass the selected operator's `consumer_id` (e.g. `con_stream_bh`) so the
 * Rust layer fetches the API keys for that consumer's GCP secrets.
 *
 * Tauri serialises JS camelCase keys to Rust snake_case automatically
 * (`consumerId` → `consumer_id`). When the value is `undefined`, Tauri
 * sends `null`, which the Rust `Option<String>` reads as `None`.
 *
 * Results are cached on the Rust side per consumer after first fetch.
 */
export async function getEnvironmentConfigs(
  consumerId?: string,
): Promise<EnvironmentConfigs> {
  return tauriInvoke<EnvironmentConfigs>('get_environment_configs', { consumerId })
}

// ── Operators ────────────────────────────────────────────────────────────────

/** A Stream operator (human user with their own backend consumer + API key). */
export interface Operator {
  consumer_id: string
  display_name: string
  initials: string
}

/**
 * Return the canonical list of Stream operators rendered on the operator
 * selector splash screen. The list is hardcoded at build time in the Rust
 * `services::auth::OPERATORS` table and resolved via the `list_operators`
 * Tauri command.
 */
export async function listOperators(): Promise<Operator[]> {
  return tauriInvoke<Operator[]>('list_operators')
}

// ── Staff Profile Bootstrap ──────────────────────────────────────────────────

/**
 * A staff member's profile returned by `GET /v1/auth/staff/me`.
 *
 * Mirrors the `staffProfileResponse` Go struct. `consumer_id` is absent when
 * the account exists but no API key has been provisioned yet.
 */
export interface StaffProfile {
  id: string
  object: 'staff'
  email: string
  name?: string
  role:
    | 'admin'
    | 'junior_dev'
    | 'head_of_customer_service'
    | 'svp_production_operations'
    | 'vp_marketing'
  consumer_id?: string
}

/**
 * Resolve the signed-in Google identity to a staff profile.
 *
 * Calls the `get_staff_profile` Tauri command which exchanges the stored
 * Google refresh token for a fresh ID token, then calls the backend bootstrap
 * endpoint `GET /v1/auth/staff/me`.
 *
 * Rejects with:
 * - `"staff_not_found"` — @kodeon.ai account has no staff record yet.
 * - `"not_signed_in"` — no Google refresh token in the Keychain.
 * - `"network_error"` — transient failure; safe to retry.
 * - Other strings — definitive failures (invalid token, server error, etc.)
 */
export async function getStaffProfile(): Promise<StaffProfile> {
  return tauriInvoke<StaffProfile>('get_staff_profile')
}

// ── Debug Server (task 2901) ─────────────────────────────────────────────────

/** Static info about the always-on debug HTTP server. */
export interface DebugServerInfo {
  /** Bound TCP port. 9999 in dev, 9998 in deployed builds. */
  port: number
  /** Build mode the Rust binary was compiled in. */
  mode: 'dev' | 'deployed'
}

/**
 * Get the bound port and build mode of the always-on debug HTTP server.
 *
 * The server itself is started by Rust at app boot and bound to 127.0.0.1.
 * This invoke does not start or stop the server — it only reports static
 * compile-time info so the frontend status indicator can show the bound
 * port without hard-coding it.
 *
 * See task 2901 for the debug server's purpose and wire contract.
 */
export async function getDebugServerInfo(): Promise<DebugServerInfo> {
  return tauriInvoke<DebugServerInfo>('get_debug_server_info')
}
